'use client'

import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {Section} from '@/components/common/content/Section'
import {Hero} from '@/components/common/hero/Hero'
import {MostRecentArticles} from '@/components/common/recent-articles/MostRecentArticles'
import {Page} from '@/components/Page'
import {QuestionsAndAnswersRelatedQuestions} from '@/components/questions-and-answers/QuestionsAndAnswersRelatedQuestions'
import {RelatedIpCasesBySubcategory} from '@/components/trademark-disputes/subcategory/RelatedIpCasesBySubcategory'
import {TrademarkDisputesBreadcrumbs} from '@/components/trademark-disputes/TrademarkDisputesBreadcrumbs'
import {getTrademarkDisputesContent} from '@/content/trademarkDisputes'
import {useTranslation} from '@/i18n'
import {RecentArticleCard} from '@/utils/contentful'
import {QuestionsAndAnswersContentType} from '@/utils/questions-and-answers/constants'
import {QuestionsAndAnswersRelatedQuestion} from '@/utils/questions-and-answers/types'
import {
  TrademarkDisputeCaseOverview,
  TrademarkDisputeCategoryDetail,
} from '@/utils/trademark-disputes/types'

type TrademarkDisputeJudicialPartyPageProps = {
  breadcrumbTrail: BreadcrumbStep[]
  judicialParty: TrademarkDisputeCategoryDetail
  ipCases: TrademarkDisputeCaseOverview[]
  relatedQuestions: QuestionsAndAnswersRelatedQuestion[]
  relatedArticles: RecentArticleCard[]
}

const TrademarkDisputeJudicialPartyPage: FC<TrademarkDisputeJudicialPartyPageProps> = ({
  breadcrumbTrail,
  judicialParty,
  ipCases,
  relatedQuestions,
  relatedArticles,
}) => {
  const {t} = useTranslation()
  const {
    ipCasesFaqTitle,
    relatedArticlesTitle,
    judicialParty: {
      selected: {heroTitle},
    },
  } = getTrademarkDisputesContent()

  const {name, relatedService} = judicialParty

  const hasArticles = relatedArticles.length > 0
  const hasQuestions = relatedQuestions.length > 0

  return (
    <Page>
      <Hero
        title={t(heroTitle, {judicialParty: name})}
        maxWidth="unset"
        breadcrumbs={<TrademarkDisputesBreadcrumbs trail={breadcrumbTrail} />}
      />
      <Section hasPaddingTop hasPaddingBottom={!hasArticles && !hasQuestions}>
        <RelatedIpCasesBySubcategory relatedCases={ipCases} relatedService={relatedService} />
      </Section>
      {hasArticles && (
        <Section hasPaddingTop hasPaddingBottom={!hasQuestions}>
          <MostRecentArticles
            articlesTitle={t(relatedArticlesTitle)}
            articles={relatedArticles}
            trackedEvent={{
              name: 'RelatedBlogs-TrademarkDisputeJudicialParty',
              parameters: {judicialParty: name},
            }}
          />
        </Section>
      )}
      {hasQuestions && (
        <Section hasPaddingTop hasPaddingBottom isHighlighted>
          <QuestionsAndAnswersRelatedQuestions
            type={QuestionsAndAnswersContentType.trademark}
            title={t(ipCasesFaqTitle)}
            relatedQuestions={relatedQuestions}
          />
        </Section>
      )}
    </Page>
  )
}

export default TrademarkDisputeJudicialPartyPage
